'use client';
import React, { useEffect, useRef, useState } from 'react';
import { useWindowSize } from '@coverright/utils/hooks/use-window-size/use-window-size';
import { Box, IconButton, IconButtonProps, Stack, SxProps } from '@mui/material';
import { CaretCircleLeft } from '@phosphor-icons/react/dist/ssr/CaretCircleLeft';
import { Circle } from '@phosphor-icons/react/dist/ssr/Circle';
import { styled } from '@mui/material/styles';
import { useButtonsLogic } from './hooks/use-buttons-logic';
import { useDotsLogic } from './hooks/use-dots-logic';

interface Props {
  items: React.ReactElement[];
  pageSize?: number;
  movement?: 'one-by-one' | 'pageable';
  gap?: number;
  control?: 'buttons' | 'dots';
  dotsColor?: 'string';
  slots?: {
    dot?: React.FC<IconButtonProps>;
    button?: React.FC<IconButtonProps>;
  };
  sx?: SxProps;
}

export function Carousel(props: Props) {
  const { width = 0 } = useWindowSize();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const {gap = 3, movement = 'one-by-one', control = 'buttons', dotsColor = '#094451', pageSize: defaultPageSize = 4} = props;
  const [showControls, setShowControls] = useState(false);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pagesCount, setPagesCount] = useState(1);

  const wrapperWidth = wrapperRef.current?.clientWidth || 0;
  const containerWidth = containerRef.current?.clientWidth || 0;
  const itemWidth = (wrapperWidth + gap * 8) / pageSize - gap * 8;
  const diff = containerWidth - wrapperWidth;

  const [wrapperHeight, setWrapperHeight] = useState(containerRef?.current?.clientHeight || 0);

  const recalcPagesCount = () => {
    const itemsVisibleCount = Math.floor(wrapperWidth / (itemRef.current?.clientWidth || 1));
    const innPageSize = itemsVisibleCount && itemsVisibleCount < defaultPageSize ? itemsVisibleCount : defaultPageSize

    setPageSize(innPageSize)
    setPagesCount(Math.ceil(props.items.length / innPageSize))
    setShowControls(innPageSize < props.items.length)
  }

  useEffect(() => {
    setOffset(0);
    recalcPagesCount();
    setWrapperHeight(prev =>containerRef?.current?.clientHeight || prev)
  }, [width, props.items]);

  const {handleRightClick, handleLeftClick, rightButtonsDisabled, leftButtonsDisabled} = useButtonsLogic(offset, setOffset, containerWidth, wrapperWidth, diff, itemWidth, gap, pageSize, movement)
  const {handleDotClick, currentPage} = useDotsLogic(setOffset, itemWidth, gap, pageSize, containerWidth, wrapperWidth)

  const ButtonComponent = props.slots?.button || Button;

  return <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      flexDirection: props.control === 'dots' ? 'column' : 'row',
      width: 1,
      gap: 2,
      ...(props.sx || {})
    }}
  >
    {control === 'buttons' && (
      <ButtonComponent disabled={leftButtonsDisabled}
                  sx={{ opacity: showControls ? 1 : 0 }}
                  onClick={handleLeftClick} />
    )}
    <Wrapper className={'carousel-wrapper'} ref={wrapperRef} sx={{height: wrapperHeight}}>
      <ItemsContainer className={'carousel-items-container'} ref={containerRef} sx={{ left: offset, gap }}>
        {props.items.map((item, i) => (
          <React.Fragment key={i}>
            {React.cloneElement(item, { ref: itemRef, sx: {width: itemWidth} })}
          </React.Fragment>
        ))}
      </ItemsContainer>
    </Wrapper>

    {control === 'buttons' && (
      <ButtonComponent disabled={rightButtonsDisabled}
                       sx={{ opacity: showControls ? 1 : 0, transform: 'rotate(180deg)' }}
                       onClick={handleRightClick} />
    )}

    {showControls && control === 'dots' && (
      <Stack direction={'row'} flexWrap={'wrap'} justifyContent={'center'}>
        {Array(pagesCount || 0).fill(1).map((o, i) => (
          <DotButton key={i} onClick={() => handleDotClick(i)} sx={{transform: currentPage === i ? 'scale(1.3)' : 'inherit'}} />
        ))}
      </Stack>
    )}
  </Box>
}

const Button = (props: IconButtonProps) => (
  <IconButton size={'small'} className={'carousel-button'} {...props}>
    <CaretCircleLeft
      style={{ opacity: props.disabled ? 0.3 : 1 }}
      size={32}
      color={'#094451'}
    />
  </IconButton>
)

const DotButton = (props: IconButtonProps) => (
  <IconButton size={'small'} className={'carousel-dot-button'} {...props} sx={{transition: 'all .3s', '&:hover': {transform: 'scale(1.3)' }, ...(props.sx || {}) }}>
    <Circle weight={'fill'}
      size={10}
      color={'#094451'}
    />
  </IconButton>
)

const Wrapper = styled(Box)({
  position: 'relative',
  width: '100%',
  overflowX: 'hidden',
  overflowY: 'clip'
});

const ItemsContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  display: 'flex',
  transition: 'all 1s',
  minWidth: '100%'
});
